import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import BlogPostPreview from './BlogPostPreview';
import { Newspaper, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaOptionsType, EmblaCarouselType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import { GhostPost, ghostService } from '@/lib/ghostService';

// Embla carousel options
const OPTIONS: EmblaOptionsType = {
  loop: true,
  align: 'center',
  skipSnaps: false,
  dragFree: false,
  containScroll: 'trimSnaps',
  slidesToScroll: 1,
  watchDrag: true,
  watchResize: true,
  watchSlides: true,
};

export default function BlogPosts() {
  const [posts, setPosts] = useState<GhostPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Embla carousel state
  const [emblaRef, emblaApi] = useEmblaCarousel(
    OPTIONS,
    [Autoplay({
      delay: 5000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
      playOnInit: true
    })]
  );
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  // Carousel navigation functions
  const scrollPrev = useCallback(() => {
    if (emblaApi && emblaApi.canScrollPrev()) {
      emblaApi.scrollPrev();
      console.log('scrollPrev called');
    }
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi && emblaApi.canScrollNext()) {
      emblaApi.scrollNext();
      console.log('scrollNext called');
    }
  }, [emblaApi]);

  const scrollTo = useCallback((index: number) => {
    if (emblaApi) {
      emblaApi.scrollTo(index);
      console.log('scrollTo called with index:', index);
    }
  }, [emblaApi]);

  // Fetch posts on component mount
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await ghostService.getPosts(6); // Get more posts for the carousel
        setPosts(response.posts);
        setError(null);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Nepodarilo sa načítať najnovšie články. Skúste to prosím neskôr.');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Debug function to help troubleshoot carousel issues
  useEffect(() => {
    if (emblaApi) {
      console.log('Embla carousel initialized');
      console.log('Embla scrollSnaps:', emblaApi.scrollSnapList());
      console.log('Can scroll prev:', emblaApi.canScrollPrev());
      console.log('Can scroll next:', emblaApi.canScrollNext());

      // Add click and touch event listeners to manually handle touch events
      const rootNode = emblaApi.rootNode();
      const containerNode = emblaApi.containerNode();

      // Log user interactions for debugging
      rootNode.addEventListener('mousedown', () => console.log('Mouse down on carousel'));
      rootNode.addEventListener('touchstart', () => console.log('Touch start on carousel'));

      // Add explicit event listener for selection changes
      emblaApi.on('select', () => {
        console.log('Slide changed to:', emblaApi.selectedScrollSnap());
        setSelectedIndex(emblaApi.selectedScrollSnap());
      });
    }
  }, [emblaApi]);

  // Update carousel controls when emblaApi changes
  const onUpdate = useCallback((emblaApiInstance: EmblaCarouselType) => {
    if (!emblaApiInstance) return;
    setScrollSnaps(emblaApiInstance.scrollSnapList());
    setSelectedIndex(emblaApiInstance.selectedScrollSnap());
    setPrevBtnEnabled(emblaApiInstance.canScrollPrev());
    setNextBtnEnabled(emblaApiInstance.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onUpdate(emblaApi); // Initial setup
    emblaApi.on('select', onUpdate); // Update on slide change
    emblaApi.on('reInit', onUpdate); // Update on reinitialization

    // Force a reinitialization after a short delay to ensure proper setup
    const timer = setTimeout(() => {
      emblaApi.reInit();
      console.log('Embla carousel reinitialized');

      // After reinitialization, also scroll to the first slide to ensure proper state
      emblaApi.scrollTo(0, true); // true for immediate scroll without animation
      onUpdate(emblaApi);

      // Add additional logging
      console.log('After reinit - selected index:', emblaApi.selectedScrollSnap());
      console.log('After reinit - can scroll prev:', emblaApi.canScrollPrev());
      console.log('After reinit - can scroll next:', emblaApi.canScrollNext());
      console.log('After reinit - slides visible:', emblaApi.slidesInView());

      // Add a second reinit after a longer delay to ensure everything is properly sized
      setTimeout(() => {
        emblaApi.reInit();
        console.log('Second reinitialization complete');
      }, 1000);
    }, 500);

    return () => {
      clearTimeout(timer);
      emblaApi.off('select', onUpdate);
      emblaApi.off('reInit', onUpdate);
    };
  }, [emblaApi, onUpdate]);

  return (
    <section id="blog" className="py-24 bg-muted/30 dark:bg-gray-800/40">
      <div className="container px-4 mx-auto">
        <div className="flex justify-between items-center mb-8">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl font-bold dark:text-white"
          >
            NEWS zo sveta AI a vzdelávania
          </motion.h2>

          {/* Carousel navigation buttons */}
          {emblaApi && posts.length > 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex gap-2"
            >
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  scrollPrev();
                  // Force UI update
                  if (emblaApi) {
                    setSelectedIndex(emblaApi.selectedScrollSnap());
                  }
                }}
                className="rounded-full disabled:opacity-50 dark:border-gray-600 dark:text-white"
                aria-label="Predchádzajúci článok"
                disabled={!prevBtnEnabled}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  scrollNext();
                  // Force UI update
                  if (emblaApi) {
                    setSelectedIndex(emblaApi.selectedScrollSnap());
                  }
                }}
                className="rounded-full disabled:opacity-50 dark:border-gray-600 dark:text-white"
                aria-label="Nasledujúci článok"
                disabled={!nextBtnEnabled}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </motion.div>
          )}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-gray-600 dark:text-gray-300 text-center max-w-3xl mx-auto mb-12"
        >

        </motion.p>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-6 rounded-lg text-center max-w-3xl mx-auto"
          >
            <p className="flex items-center justify-center">
              <Newspaper className="w-5 h-5 mr-2" />
              {error}
            </p>
          </motion.div>
        ) : posts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 p-6 rounded-lg text-center max-w-3xl mx-auto"
          >
            <p className="flex items-center justify-center">
              <Newspaper className="w-5 h-5 mr-2" />
              Zatiaľ nie sú k dispozícii žiadne články.
            </p>
          </motion.div>
        ) : (
          <>
            {/* Embla Carousel Structure */}
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex">
                {posts.map((post, index) => (
                  <div
                    key={post.id}
                    className="flex-none w-full sm:w-1/2 lg:w-1/3 px-3 py-4"
                    style={{ flex: '0 0 auto' }}
                  >
                    <BlogPostPreview post={post} index={index} />
                  </div>
                ))}
              </div>
            </div>

            {/* Dot Indicators */}
            {scrollSnaps.length > 1 && (
              <div className="flex justify-center mt-8 gap-2">
                {scrollSnaps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      if (emblaApi) {
                        // Force a reinitialization before scrolling to ensure proper state
                        emblaApi.reInit();
                        setTimeout(() => {
                          emblaApi.scrollTo(index);
                          console.log('Scrolling to index:', index);
                          setSelectedIndex(index);
                        }, 50);
                      }
                    }}
                    className={`h-2 rounded-full transition-all duration-300 ease-out ${index === selectedIndex ? 'w-4 bg-primary' : 'w-2 bg-muted-foreground/50 hover:bg-muted-foreground/75'
                      }`}
                    aria-label={`Prejsť na článok ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center mt-10"
        >
          <Button
            size="lg"
            className="group inline-flex items-center"
            onClick={() => window.location.href = '/blog'}
          >
            Všetky články
            <Newspaper className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
}